import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, Globe, Star, MessageSquare, Edit, Trash2, CalendarCheck, BadgeCheck, Share2, X } from 'lucide-react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import axios from 'axios';

// Fix Leaflet icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface Review {
  id: string;
  rating: number;
  comment?: string;
  visitDate?: string;
  wouldRecommend: boolean;
  isAnonymous: boolean;
  createdAt: string;
  user: {
    firstName?: string;
    lastName?: string;
  };
}

interface Specialist {
  id: string;
  name: string;
  title?: string;
  specialtyType: string;
  phone: string;
  email?: string;
  website?: string;
  avatarUrl?: string;
  address: string;
  city: string;
  county: string;
  latitude: number;
  longitude: number;
  description?: string;
  education?: string;
  experience?: string;
  averageRating: number;
  totalReviews: number;
  acceptsNewPatients: boolean;
  specializations: string[];
  reviews: Review[];
  submittedById?: string;
}

const specialtyLabels: Record<string, string> = {
  // Doctori
  FAMILY_DOCTOR:         'Medic de familie',
  INTERNAL_MEDICINE:     'Medicină internă',
  RHEUMATOLOGIST:        'Reumatolog',
  IMMUNOLOGIST:          'Imunolog',
  ALLERGOLOGIST:         'Alergolog',
  NEUROLOGIST:           'Neurolog',
  CARDIOLOGIST:          'Cardiolog',
  PULMONOLOGIST:         'Pneumolog',
  GASTROENTEROLOGIST:    'Gastroenterolog',
  ENDOCRINOLOGIST:       'Endocrinolog',
  DIABETOLOGIST:         'Diabetolog',
  NEPHROLOGIST:          'Nefrolog',
  UROLOGIST:             'Urolog',
  GYNECOLOGIST:          'Ginecolog',
  DERMATOLOGIST:         'Dermatolog',
  HEMATOLOGIST:          'Hematolog',
  PSYCHIATRIST:          'Psihiatru',
  ENT_DOCTOR:            'ORL',
  OPHTHALMOLOGIST:       'Oftalmolog',
  ONCOLOGIST:            'Oncolog',
  GENETICIST:            'Medic genetician',
  PEDIATRICIAN:          'Pediatru',
  GERIATRICIAN:          'Geriatru',
  ORTHOPEDIST:           'Ortoped',
  REHABILITATION_DOCTOR: 'Medic recuperare medicală',
  // Specialiști
  PHYSIOTHERAPIST:        'Kinetoterapeut',
  PHYSICAL_THERAPIST:     'Fizioterapeut',
  OCCUPATIONAL_THERAPIST: 'Ergoterapeut',
  PSYCHOLOGIST:           'Psiholog',
  PSYCHOTHERAPIST:        'Psihoterapeut',
  NUTRITIONIST:           'Nutriționist',
  DIETITIAN:              'Dietetician',
  SPEECH_THERAPIST:       'Logoped',
  MASSAGE_THERAPIST:      'Maseur medical',
  SOCIAL_WORKER:          'Asistent social',
  GENETIC_COUNSELOR:      'Consilier genetic',
  HOME_CAREGIVER:         'Îngrijitor la domiciliu',
};

export default function SpecialistDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const [specialist, setSpecialist] = useState<Specialist | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [reviewData, setReviewData] = useState({
    rating: 5,
    comment: '',
    visitDate: '',
    wouldRecommend: true,
    isAnonymous: false
  });
  const [editData, setEditData] = useState({
    name: '',
    title: '',
    specialtyType: '',
    phone: '',
    email: '',
    website: '',
    address: '',
    city: '',
    county: '',
    description: '',
    education: '',
    experience: '',
    acceptsNewPatients: true,
    specializations: '',
    avatarUrl: ''
  });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Booking modal state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingData, setBookingData] = useState({
    date: '',
    time: '',
    reason: '',
    name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
    phone: '',
    notes: '',
  });
  const [bookingSubmitted, setBookingSubmitted] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);

  useEffect(() => {
    fetchSpecialist();
    trackView();
  }, [id]);

  const trackView = async () => {
    try {
      const token = localStorage.getItem('accessToken') || accessToken;
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/specialists/${id}/view`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      // Silently fail - tracking is not critical
      console.log('View tracking failed');
    }
  };

  // Populate edit form when specialist loads or modal opens
  useEffect(() => {
    if (specialist && showEditModal) {
      setEditData({
        name: specialist.name,
        title: specialist.title || '',
        specialtyType: specialist.specialtyType,
        phone: specialist.phone,
        email: specialist.email || '',
        website: specialist.website || '',
        address: specialist.address,
        city: specialist.city,
        county: specialist.county,
        description: specialist.description || '',
        education: specialist.education || '',
        experience: specialist.experience || '',
        acceptsNewPatients: specialist.acceptsNewPatients,
        specializations: specialist.specializations.join(', '),
        avatarUrl: specialist.avatarUrl || ''
      });
      setAvatarPreview(specialist.avatarUrl ? `${import.meta.env.VITE_API_URL}${specialist.avatarUrl}` : '');
    }
  }, [specialist, showEditModal]);

  const fetchSpecialist = async () => {
    try {
      const token = localStorage.getItem('accessToken') || accessToken;
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/specialists/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSpecialist(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('accessToken') || accessToken;
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/specialists/${id}/reviews`,
        reviewData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Review adăugat cu succes!');
      setShowReviewForm(false);
      fetchSpecialist();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Eroare la adăugare review');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Sigur vrei să ștergi acest specialist?')) return;
    
    try {
      const token = localStorage.getItem('accessToken') || accessToken;
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/api/specialists/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Specialist șters cu succes!');
      navigate('/specialists');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Eroare la ștergere');
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return null;
    
    setUploading(true);
    try {
      const token = localStorage.getItem('accessToken') || accessToken;
      const formData = new FormData();
      formData.append('file', avatarFile);
      
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/specialists/upload-avatar`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      return response.data.avatarUrl;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const token = localStorage.getItem('accessToken') || accessToken;
      
      // Upload new avatar if selected
      let avatarUrl = editData.avatarUrl;
      if (avatarFile) {
        avatarUrl = await uploadAvatar();
      }
      
      await axios.put(
        `${import.meta.env.VITE_API_URL}/api/specialists/${id}`,
        {
          ...editData,
          avatarUrl,
          specializations: editData.specializations.split(',').map(s => s.trim()).filter(Boolean)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Specialist actualizat cu succes! ✅');
      setShowEditModal(false);
      setAvatarFile(null);
      setAvatarPreview('');
      fetchSpecialist();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Eroare la actualizare');
    } finally {
      setUpdating(false);
    }
  };

  const canEdit = user && (user.role === 'ADMIN' || (specialist && specialist.submittedById === user.id));

  const romanianCounties = [
    'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani', 'Brașov',
    'Brăila', 'București', 'Buzău', 'Caraș-Severin', 'Călărași', 'Cluj', 'Constanța',
    'Covasna', 'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu', 'Gorj', 'Harghita', 'Hunedoara',
    'Ialomița', 'Iași', 'Ilfov', 'Maramureș', 'Mehedinți', 'Mureș', 'Neamț', 'Olt',
    'Prahova', 'Satu Mare', 'Sălaj', 'Sibiu', 'Suceava', 'Teleorman', 'Timiș', 'Tulcea',
    'Vaslui', 'Vâlcea', 'Vrancea'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Se încarcă...</p>
      </div>
    );
  }

  if (!specialist) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Specialist negăsit</p>
          <button onClick={() => navigate('/specialists')} className="text-blue-600">
            ← Înapoi la listă
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title={`${specialist.title ? specialist.title + ' ' : ''}${specialist.name}`}
        subtitle={specialtyLabels[specialist.specialtyType]}
        showBack={true}
        backTo="/specialists"
      />

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0 mx-auto md:mx-0">
              {specialist.avatarUrl ? (
                <img
                  src={`${import.meta.env.VITE_API_URL}${specialist.avatarUrl}`}
                  alt={specialist.name}
                  className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-4 border-white shadow-lg"
                />
              ) : (
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-2xl md:text-3xl border-4 border-white shadow-lg">
                  {specialist.name.charAt(0)}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 flex flex-col md:flex-row md:justify-between md:items-start gap-4">
              <div className="text-center md:text-left">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {specialist.title && `${specialist.title} `}{specialist.name}
                </h1>
                <p className="text-base md:text-lg text-blue-600 mt-1 md:mt-2">
                  {specialtyLabels[specialist.specialtyType]}
                </p>
              </div>
              
              <div className="flex flex-col md:flex-row items-center gap-3">
                {/* Rating - Compact pe mobil */}
                <div className="flex items-center gap-2 bg-yellow-50 px-3 md:px-4 py-2 rounded-lg">
                  <Star className="w-5 h-5 md:w-6 md:h-6 text-yellow-500 fill-current" />
                  <span className="text-xl md:text-2xl font-bold">{specialist.averageRating.toFixed(1)}</span>
                  <span className="text-sm md:text-base text-gray-600">({specialist.totalReviews})</span>
                </div>
                
                {/* Edit/Delete buttons - Grid 2 coloane pe mobil */}
                {canEdit && (
                  <div className="grid grid-cols-2 md:flex gap-2 w-full md:w-auto">
                    <button
                      onClick={() => setShowEditModal(true)}
                      className="px-3 md:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 text-sm md:text-base"
                    >
                      <Edit size={16} className="md:w-[18px] md:h-[18px]" />
                      <span className="hidden sm:inline">Editează</span>
                      <span className="sm:hidden">Edit</span>
                    </button>
                    <button
                      onClick={handleDelete}
                      className="px-3 md:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2 text-sm md:text-base"
                    >
                      <Trash2 size={16} className="md:w-[18px] md:h-[18px]" />
                      <span className="hidden sm:inline">Șterge</span>
                      <span className="sm:hidden">Delete</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact & Location */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4">Contact & Locație</h2>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <span>{specialist.address}, {specialist.city}, {specialist.county}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <a href={`tel:${specialist.phone}`} className="text-blue-600 hover:underline">
                    {specialist.phone}
                  </a>
                </div>
                {specialist.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <a href={`mailto:${specialist.email}`} className="text-blue-600 hover:underline">
                      {specialist.email}
                    </a>
                  </div>
                )}
                {specialist.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-gray-400" />
                    <a href={specialist.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {specialist.website}
                    </a>
                  </div>
                )}
              </div>

              {/* Mini Map */}
              <div className="mt-6 h-64 rounded-lg overflow-hidden">
                <MapContainer
                  center={[specialist.latitude, specialist.longitude]}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Marker position={[specialist.latitude, specialist.longitude]} />
                </MapContainer>
              </div>
            </div>

            {/* Description */}
            {specialist.description && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-4">Despre</h2>
                <p className="text-gray-700 whitespace-pre-line">{specialist.description}</p>
              </div>
            )}

            {/* Reviews */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Review-uri ({specialist.totalReviews})</h2>
                <button
                  onClick={() => setShowReviewForm(!showReviewForm)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <MessageSquare className="w-4 h-4 inline mr-2" />
                  Scrie review
                </button>
              </div>

              {/* Review Form */}
              {showReviewForm && (
                <form onSubmit={handleSubmitReview} className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Rating</label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewData({...reviewData, rating: star})}
                            className="text-3xl"
                          >
                            <Star className={star <= reviewData.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'} />
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Comentariu</label>
                      <textarea
                        value={reviewData.comment}
                        onChange={(e) => setReviewData({...reviewData, comment: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg"
                        rows={4}
                        placeholder="Împărtășește experiența ta..."
                      />
                    </div>

                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={reviewData.wouldRecommend}
                          onChange={(e) => setReviewData({...reviewData, wouldRecommend: e.target.checked})}
                        />
                        <span className="text-sm">Recomand</span>
                      </label>
                      
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={reviewData.isAnonymous}
                          onChange={(e) => setReviewData({...reviewData, isAnonymous: e.target.checked})}
                        />
                        <span className="text-sm">Review anonim</span>
                      </label>
                    </div>

                    <div className="flex gap-2">
                      <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                        Trimite
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowReviewForm(false)}
                        className="px-4 py-2 bg-gray-200 rounded-lg"
                      >
                        Anulează
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {/* Reviews List */}
              <div className="space-y-4">
                {specialist.reviews.map(review => (
                  <div key={review.id} className="border-b pb-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold">
                          {review.isAnonymous ? 'Anonim' : `${review.user.firstName || ''} ${review.user.lastName || ''}`}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`} />
                            ))}
                          </div>
                          <span className="text-sm text-gray-500">
                            {new Date(review.createdAt).toLocaleDateString('ro-RO')}
                          </span>
                        </div>
                      </div>
                    </div>
                    {review.comment && (
                      <p className="text-gray-700 mt-2">{review.comment}</p>
                    )}
                    {review.wouldRecommend && (
                      <span className="inline-block mt-2 text-sm text-green-600">✓ Recomandă</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">

            {/* ── BOOKING CTA ── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-2 mb-1">
                <BadgeCheck className="w-4 h-4 text-teal-500" />
                <span className="text-xs font-semibold text-teal-600 uppercase tracking-wide">FII FIT Verificat</span>
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-1">
                {specialist.title ? `${specialist.title} ${specialist.name}` : specialist.name}
              </h3>
              <p className="text-sm text-gray-500 mb-4">{specialtyLabels[specialist.specialtyType]}</p>

              {specialist.acceptsNewPatients ? (
                <>
                  <div className="flex items-center gap-2 mb-4 text-green-700 bg-green-50 px-3 py-2 rounded-lg text-sm font-semibold">
                    <span className="w-2 h-2 bg-green-500 rounded-full inline-block"></span>
                    Acceptă clienți noi
                  </div>
                  <button
                    onClick={() => { setShowBookingModal(true); setBookingSubmitted(false); }}
                    className="w-full bg-teal-600 text-white py-3.5 rounded-xl font-bold hover:bg-teal-700 transition shadow-lg flex items-center justify-center gap-2 text-base"
                  >
                    <CalendarCheck className="w-5 h-5" />
                    Programează-te
                  </button>
                </>
              ) : (
                <div className="px-4 py-3 bg-gray-100 text-gray-500 rounded-xl text-center text-sm font-semibold">
                  Nu acceptă clienți noi momentan
                </div>
              )}

              <div className="mt-4 grid grid-cols-2 gap-2">
                <a
                  href={`tel:${specialist.phone}`}
                  className="flex items-center justify-center gap-2 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50 transition"
                >
                  <Phone className="w-4 h-4" />
                  Sună
                </a>
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({ title: specialist.name, url: window.location.href });
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                      alert('Link copiat!');
                    }
                  }}
                  className="flex items-center justify-center gap-2 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50 transition"
                >
                  <Share2 className="w-4 h-4" />
                  Distribuie
                </button>
              </div>
            </div>

            {/* Status */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-semibold mb-4">Status</h3>
              {specialist.acceptsNewPatients ? (
                <div className="px-4 py-3 bg-green-50 text-green-800 rounded-lg text-center">
                  ✓ Acceptă clienți noi
                </div>
              ) : (
                <div className="px-4 py-3 bg-gray-100 text-gray-600 rounded-lg text-center">
                  Nu acceptă clienți noi
                </div>
              )}
            </div>

            {/* Specializations */}
            {specialist.specializations.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="font-semibold mb-4">Specializări</h3>
                <div className="flex flex-wrap gap-2">
                  {specialist.specializations.map((spec, idx) => (
                    <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Education */}
            {specialist.education && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="font-semibold mb-4">Educație</h3>
                <p className="text-gray-700 text-sm whitespace-pre-line">{specialist.education}</p>
              </div>
            )}

            {/* Experience */}
            {specialist.experience && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="font-semibold mb-4">Experiență</h3>
                <p className="text-gray-700 text-sm whitespace-pre-line">{specialist.experience}</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* ── Booking Modal ─────────────────────────────────────────────── */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[10000]">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Programare online</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {specialist.title ? `${specialist.title} ${specialist.name}` : specialist.name}
                  </p>
                </div>
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              {bookingSubmitted ? (
                /* Success state */
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CalendarCheck className="w-8 h-8 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Cerere trimisă! 🎉</h3>
                  <p className="text-gray-600 mb-2">
                    Cererea ta de programare a fost trimisă cu succes.
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    Centrul te va contacta în cel mai scurt timp pentru confirmare.
                  </p>
                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 text-left mb-6">
                    <p className="text-sm font-semibold text-teal-800 mb-2">Detalii programare:</p>
                    <div className="space-y-1 text-sm text-teal-700">
                      <p>📅 Data: <strong>{bookingData.date}</strong></p>
                      <p>🕐 Ora: <strong>{bookingData.time}</strong></p>
                      <p>📋 Motiv: <strong>{bookingData.reason}</strong></p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowBookingModal(false)}
                    className="w-full bg-teal-600 text-white py-3 rounded-xl font-bold hover:bg-teal-700 transition"
                  >
                    Închide
                  </button>
                </div>
              ) : (
                /* Booking form */
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!bookingData.date || !bookingData.time || !bookingData.reason) return;
                    setBookingLoading(true);
                    // Simulate API call - in production connect to booking endpoint
                    await new Promise(r => setTimeout(r, 1000));
                    setBookingLoading(false);
                    setBookingSubmitted(true);
                  }}
                  className="space-y-4"
                >
                  {/* Date & Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Data dorită *
                      </label>
                      <input
                        type="date"
                        required
                        min={new Date().toISOString().split('T')[0]}
                        value={bookingData.date}
                        onChange={(e) => setBookingData({ ...bookingData, date: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Ora dorită *
                      </label>
                      <select
                        required
                        value={bookingData.time}
                        onChange={(e) => setBookingData({ ...bookingData, time: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      >
                        <option value="">Selectează...</option>
                        {['08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30',
                          '12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30',
                          '16:00','16:30','17:00','17:30'].map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Scopul programării *
                    </label>
                    <input
                      type="text"
                      required
                      value={bookingData.reason}
                      onChange={(e) => setBookingData({ ...bookingData, reason: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="ex: Evaluare inițială, antrenament personal, plan nutriție..."
                    />
                  </div>

                  {/* Patient name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Numele tău *
                    </label>
                    <input
                      type="text"
                      required
                      value={bookingData.name}
                      onChange={(e) => setBookingData({ ...bookingData, name: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Prenume Nume"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Telefon de contact *
                    </label>
                    <input
                      type="tel"
                      required
                      value={bookingData.phone}
                      onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="+40 7xx xxx xxx"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Observații (opțional)
                    </label>
                    <textarea
                      value={bookingData.notes}
                      onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Obiective fitness, nivel de experiență, alte informații..."
                    />
                  </div>

                  {/* Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <p className="text-xs text-blue-700">
                      ℹ️ Cererea ta va fi transmisă centrului. Vei fi contactat pentru confirmare în cel mai scurt timp.
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowBookingModal(false)}
                      className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-50 transition"
                    >
                      Anulează
                    </button>
                    <button
                      type="submit"
                      disabled={bookingLoading}
                      className="flex-1 bg-teal-600 text-white py-3 rounded-xl font-bold hover:bg-teal-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {bookingLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Se trimite...
                        </>
                      ) : (
                        <>
                          <CalendarCheck className="w-4 h-4" />
                          Trimite cererea
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{ zIndex: 10001 }}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Editează Specialist</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setAvatarFile(null);
                    setAvatarPreview('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleUpdate} className="space-y-4">
                {/* Avatar Upload */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Avatar (optional)</label>
                  <div className="flex items-center gap-4">
                    {avatarPreview && (
                      <img 
                        src={avatarPreview} 
                        alt="Avatar preview" 
                        className="w-20 h-20 rounded-full object-cover border-2 border-gray-300"
                      />
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,image/webp"
                        onChange={handleAvatarChange}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">JPEG, PNG sau WebP, max 5MB</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nume *</label>
                    <input
                      required
                      value={editData.name}
                      onChange={(e) => setEditData({...editData, name: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Titlu</label>
                    <input
                      value={editData.title}
                      onChange={(e) => setEditData({...editData, title: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Dr., Conf. Dr., Prof. Dr."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Tip Specialist *</label>
                    <select
                      required
                      value={editData.specialtyType}
                      onChange={(e) => setEditData({...editData, specialtyType: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      {Object.entries(specialtyLabels).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Telefon *</label>
                    <input
                      required
                      type="tel"
                      value={editData.phone}
                      onChange={(e) => setEditData({...editData, phone: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <input
                      type="email"
                      value={editData.email}
                      onChange={(e) => setEditData({...editData, email: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Website</label>
                    <input
                      type="url"
                      value={editData.website}
                      onChange={(e) => setEditData({...editData, website: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Adresă *</label>
                  <input
                    required
                    value={editData.address}
                    onChange={(e) => setEditData({...editData, address: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Oraș *</label>
                    <input
                      required
                      value={editData.city}
                      onChange={(e) => setEditData({...editData, city: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Județ *</label>
                    <select
                      required
                      value={editData.county}
                      onChange={(e) => setEditData({...editData, county: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      {romanianCounties.map(county => (
                        <option key={county} value={county}>{county}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Descriere (opțional)</label>
                  <textarea
                    value={editData.description}
                    onChange={(e) => setEditData({...editData, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={3}
                    placeholder="Scurtă descriere despre practica ta..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Educație (opțional)</label>
                  <textarea
                    value={editData.education}
                    onChange={(e) => setEditData({...editData, education: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={3}
                    placeholder="Certificări, specializări fitness, cursuri absolvite..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Experiență (opțional)</label>
                  <textarea
                    value={editData.experience}
                    onChange={(e) => setEditData({...editData, experience: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={3}
                    placeholder="Ani de experiență, locuri de muncă anterioare..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Specializări (separate prin virgulă, opțional)</label>
                  <input
                    value={editData.specializations}
                    onChange={(e) => setEditData({...editData, specializations: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Fitness, Nutriție Sport, Yoga, CrossFit..."
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editData.acceptsNewPatients}
                      onChange={(e) => setEditData({...editData, acceptsNewPatients: e.target.checked})}
                    />
                    <span className="text-sm">Acceptă clienți noi</span>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={updating || uploading}
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {uploading ? 'Se încarcă imaginea...' : updating ? 'Se actualizează...' : 'Salvează Modificările'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setAvatarFile(null);
                      setAvatarPreview('');
                    }}
                    className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                  >
                    Anulează
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
